import fp from "lodash/fp";
import { getFormValues, touch } from "redux-form";

import { formatE164, getResidualsRole } from "helpers";
import { mapFrontToBackUserGeneralInfoTab } from "models/systemSetup/user";
import { usersService } from "services";
import { usersConstants } from "store";

export const usersActions = {
	submitAddUserForm,
	updateGeneralInfo,
	updateLinks,
	setActiveUserId,
};

function submitAddUserForm(generalInfoFormValues, generalInfoFormSyncErrors) {
	return (dispatch, getState) => {
		dispatch(request());
		const state = getState();

		if (!fp.isEmpty(generalInfoFormSyncErrors)) {
			return Promise.reject("Validation failed");
		}

		const rolePermissionsFormValues =
			getFormValues("addUser.rolePermissions")(state) || {};

		const checkedPermissionsCount = fp.flow(
			fp.property("permissions"),
			fp.filter(p => p.isChecked),
			fp.size,
		)(rolePermissionsFormValues);

		const isResidualsRole = getResidualsRole(
			rolePermissionsFormValues?.role?.name,
		);
		if (checkedPermissionsCount === 0 && !isResidualsRole) {
			dispatch(touch("addUser.rolePermissions", "permissions"));
			return Promise.reject("Validation failed");
		}

		const activeFormValues = getFormValues("addUser.active")(state) || {};

		const merchantLinksFormValues = getFormValues("addUser.merchantLinks")(
			state,
		) || { merchantAgents: [] };

		const user = {
			...generalInfoFormValues,
			...activeFormValues,
			...merchantLinksFormValues,
			roleId: rolePermissionsFormValues?.role?.id,
			isAgent: rolePermissionsFormValues?.isAgent,
			mobilePhone: formatE164(generalInfoFormValues?.mobilePhone) || null,
			phone: formatE164(generalInfoFormValues?.phone) || null,
		};

		const permissionsId = [];
		rolePermissionsFormValues.permissions?.forEach(permission => {
			permission.isChecked && permissionsId.push(permission.id);
		});

		user.personalPermissions = permissionsId;

		user.userMerchants =
			merchantLinksFormValues?.userMerchants?.map(m => ({
				merchantId: m.id,
				statementsAllowed: merchantLinksFormValues.statementsAllowed,
			})) ?? [];

		user.agentOfficeId = user.agentOffice?.id;
		delete user.agentOffice;

		if (isResidualsRole) {
			delete user?.personalPermissions;
		}

		return usersService.addUser(user).then(
			createdUser => {
				dispatch(success());
				return Promise.resolve(createdUser);
			},
			error => {
				dispatch(failure(error));
				return Promise.reject(error);
			},
		);
	};

	function request() {
		return {
			type: usersConstants.ADD_USER_SUBMIT_REQUEST,
		};
	}

	function success() {
		return {
			type: usersConstants.ADD_USER_SUBMIT_SUCCESS,
		};
	}

	function failure(error) {
		return {
			type: usersConstants.ADD_USER_SUBMIT_FAILURE,
			error,
		};
	}
}

function updateGeneralInfo({ id, ...generalInfo }) {
	return dispatch => {
		dispatch(request());

		return usersService
			.updateUserInfo({
				id,
				...mapFrontToBackUserGeneralInfoTab(generalInfo),
			})
			.then(
				res => {
					dispatch(success());
					return Promise.resolve(res);
				},
				error => {
					dispatch(failure(error));
					return Promise.reject(error);
				},
			);
	};

	function request() {
		return {
			type: usersConstants.UPDATE_GENERAL_INFO_REQUEST,
		};
	}

	function success() {
		return {
			type: usersConstants.UPDATE_GENERAL_INFO_SUCCESS,
		};
	}

	function failure(error) {
		return {
			type: usersConstants.UPDATE_GENERAL_INFO_FAILURE,
			error,
		};
	}
}

function updateLinks({ id, userMerchants, ...otherLinks }) {
	return dispatch => {
		dispatch(request());

		userMerchants = userMerchants.map(m => {
			m.merchantId = m.id;
			return m;
		});

		return usersService
			.updateUserInfo({ id, links: { userMerchants, ...otherLinks } })
			.then(
				() => {
					dispatch(success());
					return Promise.resolve();
				},
				error => {
					dispatch(failure(error));
					return Promise.reject(error);
				},
			);
	};

	function request() {
		return {
			type: usersConstants.UPDATE_LINKS_REQUEST,
		};
	}

	function success() {
		return {
			type: usersConstants.UPDATE_LINKS_SUCCESS,
		};
	}

	function failure(error) {
		return {
			type: usersConstants.UPDATE_LINKS_FAILURE,
			error,
		};
	}
}

function setActiveUserId(id) {
	return {
		type: usersConstants.SET_ACTIVE_USER_ID,
		id,
	};
}
